import { useId, useState, type FormEvent } from 'react';
import { Pencil } from 'lucide-react';
import { BUSINESS_STATUSES, type BusinessStatus } from '@quds/shared';
import { Button, FormField, Input, Modal, Select, fieldDescribedBy, useToast } from '@/components/ui';
import { updatePharmacy } from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { BUSINESS_STATUS_LABELS } from '../../components/labels';
import { PharmacyInfoFields } from '../../components/PharmacyInfoFields';
import { hasErrors, infoValuesFrom, mapFieldIssues, toInfoInput, validatePharmacyInfo, type FieldErrors, type PharmacyInfoValues } from '../../validation';
import type { ActionModalProps } from '../types';

export function EditPharmacyModal({ pharmacy, onClose }: ActionModalProps) {
  const { notify } = useToast();
  const statusId = useId();
  const registrationId = useId();
  const [values, setValues] = useState<PharmacyInfoValues>(() => infoValuesFrom(pharmacy));
  const [status, setStatus] = useState<BusinessStatus>(pharmacy.status);
  const [registrationNumber, setRegistrationNumber] = useState(pharmacy.profile.registrationNumber ?? '');
  const [errors, setErrors] = useState<FieldErrors<PharmacyInfoValues & { registrationNumber: string }>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onChange = (patch: Partial<PharmacyInfoValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    if (hasErrors(errors)) setErrors(validatePharmacyInfo(next));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const found = validatePharmacyInfo(values);
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await updatePharmacy(pharmacy.id, { ...toInfoInput(values), status, registrationNumber: registrationNumber.trim() || null });
      notify({ tone: 'success', title: 'Pharmacy updated', description: 'Changes were saved and recorded in the activity log.' });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.fieldIssues.length) setErrors(mapFieldIssues(err.fieldIssues, {}));
      setSubmitError(errorMessage(err, 'Could not save changes.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Edit Pharmacy"
      description={`${pharmacy.name} · ${pharmacy.code}`}
      icon={<Pencil size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="edit-pharmacy-form" loading={saving}>
            Save Changes
          </Button>
        </>
      }
    >
      {submitError && (
        <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {submitError}
        </p>
      )}
      <form id="edit-pharmacy-form" noValidate onSubmit={onSubmit}>
        <PharmacyInfoFields values={values} errors={errors} onChange={onChange} />
        <div className="mt-4 grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
          <FormField label="Status" htmlFor={statusId}>
            <Select
              id={statusId}
              value={status}
              onChange={(e) => setStatus(e.target.value as BusinessStatus)}
              options={BUSINESS_STATUSES.map((s) => ({ value: s, label: BUSINESS_STATUS_LABELS[s] }))}
            />
          </FormField>
          <FormField label="Registration Number" htmlFor={registrationId} error={errors.registrationNumber}>
            <Input
              id={registrationId}
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder="Optional"
              maxLength={64}
              invalid={!!errors.registrationNumber}
              aria-describedby={fieldDescribedBy(registrationId, { error: errors.registrationNumber })}
            />
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
